import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred'
      
      const customError = new Error(errorMessage)
      customError.status = error.response.status
      customError.data = error.response.data
      
      // Handle 401 unauthorized - redirect to login
      if (error.response.status === 401) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
        window.location.href = '/login'
      }
      
      return Promise.reject(customError)
    }
    
    return Promise.reject(new Error(error.message || 'Network error'))
  }
)

export const groupsApi = {
  /**
   * Get paginated list of groups with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search term
   * @param {boolean} params.is_active - Filter by active status
   * @param {number} params.page - Page number
   * @returns {Promise} API response
   */
  async getGroups(params = {}) {
    const queryParams = new URLSearchParams()
    
    if (params.search) {
      queryParams.append('search', params.search)
    }
    
    if (params.is_active !== null && params.is_active !== undefined) {
      queryParams.append('is_active', params.is_active)
    }
    
    if (params.page) {
      queryParams.append('page', params.page)
    }
    
    if (params.all) {
      queryParams.append('all', '1')
    }
    
    const queryString = queryParams.toString()
    const url = queryString ? `/groups?${queryString}` : '/groups'
    
    const response = await api.get(url)
    return response
  },

  /**
   * Get single group details including PINs
   * @param {number} id - Group ID
   * @returns {Promise} API response
   */
  async getGroup(id) {
    const response = await api.get(`/groups/${id}`)
    return response
  },

  /**
   * Create new group
   * @param {Object} groupData - Group data
   * @param {string} groupData.name - Group name
   * @param {string} groupData.description - Group description
   * @param {number} groupData.school_grade_id - School grade ID (required)
   * @param {boolean} groupData.is_active - Group active status
   * @returns {Promise} API response
   */
  async createGroup(groupData) {
    const response = await api.post('/groups', groupData)
    return response
  },

  /**
   * Update existing group
   * @param {number} id - Group ID
   * @param {Object} groupData - Updated group data
   * @param {string} groupData.name - Group name
   * @param {string} groupData.description - Group description
   * @param {number} groupData.school_grade_id - School grade ID (required)
   * @param {boolean} groupData.is_active - Group active status
   * @returns {Promise} API response
   */
  async updateGroup(id, groupData) {
    const response = await api.put(`/groups/${id}`, groupData)
    return response
  },

  /**
   * Deactivate group (set is_active to false)
   * @param {number} id - Group ID
   * @returns {Promise} API response
   */
  async deactivateGroup(id) {
    const response = await api.delete(`/groups/${id}`)
    return response
  },

  /**
   * Restore/reactivate group
   * @param {number} id - Group ID
   * @returns {Promise} API response
   */
  async restoreGroup(id) {
    const response = await api.post(`/groups/${id}/restore`)
    return response
  }
}